import React from 'react';
import { motion } from 'framer-motion';

const NoSpendChallengeLanding = ({ goToInput, theme = "mint" }) => {
  // Theme-based button styling
  const buttonStyles = {
    mint: "bg-emerald-500 hover:bg-emerald-600",
    pink: "bg-pink-500 hover:bg-pink-600",
    lavender: "bg-purple-500 hover:bg-purple-600"
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: 0.3 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <motion.div
      className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6 mb-8"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* SEO-optimized heading structure */}
      <h1 className="text-3xl font-bold text-center mb-6 dark:text-white">
        30-Day No-Spend Challenge: Break The Shopping Cycle
      </h1>
      
      {/* Hero section with main CTA */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl p-6 mb-8">
        <div className="flex flex-col md:flex-row items-center">
          <div className="mb-6 md:mb-0 md:mr-6 text-center md:text-left">
            <h2 className="text-2xl font-bold mb-3 dark:text-white">Reset Your Relationship With Spending</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Join our guided 30-day program to eliminate unnecessary purchases, discover your true spending triggers, and build lasting financial mindfulness.
            </p>
            <motion.button
              onClick={goToInput}
              className={`px-6 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Start Your Challenge
            </motion.button>
          </div>
          <div className="text-8xl">💰</div>
        </div>
      </div>
      
      {/* Stats Section */}
      <motion.section 
        className="mb-8 bg-gray-50 dark:bg-gray-700 rounded-xl p-5"
        variants={itemVariants}
      >
        <h2 className="text-xl font-bold mb-4 dark:text-white">The Impact of a No-Spend Month</h2>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold dark:text-white">$1,287</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">average savings during challenge</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">94%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">report decreased shopping urges</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">87%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">continue mindful spending after</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">30%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">reduction in impulse buys long-term</p>
          </div>
        </div>
      </motion.section>
      
      {/* How It Works Section */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">How The 30-Day Challenge Works</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">1</span>
              Set Your Challenge Rules
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Customize your no-spend rules with our guided setup. Define essential vs. non-essential purchases and set specific challenge goals.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">2</span>
              Daily Check-ins & Micro-Challenges
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Receive daily prompts, reflections, and bite-sized challenges to stay motivated and build awareness throughout your journey.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">3</span>
              Track Urges & Alternatives
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Log shopping urges as they arise and discover personalized alternative activities that fulfill the same emotional needs.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">4</span>
              Community & Accountability
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Connect with others taking the challenge, share wins and struggles, and increase your chances of success through group accountability.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">5</span>
              Post-Challenge Integration
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Complete a personalized review and receive a sustainable spending plan to maintain your progress beyond the 30 days.
            </p>
          </div>
        </div>
      </motion.section>
      
      {/* Challenge Benefits Section */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Benefits Beyond Saving Money</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">🧠</span>
              <h3 className="font-bold mt-2 dark:text-white">Mental Clarity</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              Break the dopamine-seeking cycle of constant consumption and experience mental space for creativity and focus.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">✨</span>
              <h3 className="font-bold mt-2 dark:text-white">Values Alignment</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              Rediscover what truly matters to you when shopping no longer fills the gaps created by stress, boredom, or habit.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">🌿</span>
              <h3 className="font-bold mt-2 dark:text-white">Environmental Impact</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              Reduce your consumption footprint and develop a more sustainable relationship with material goods.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">💪</span>
              <h3 className="font-bold mt-2 dark:text-white">Willpower Muscle</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              Strengthen your ability to delay gratification and make intentional choices in all areas of life.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">🏆</span>
              <h3 className="font-bold mt-2 dark:text-white">Achievement Boost</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              Experience the confidence that comes from setting a challenging goal and following through successfully.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">🤝</span>
              <h3 className="font-bold mt-2 dark:text-white">Relationship Reset</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              Discover non-consumer ways to connect with loved ones and create meaningful experiences without spending.
            </p>
          </div>
        </div>
      </motion.section>
      
      {/* Week-by-Week Breakdown */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Your 30-Day Journey: Week-by-Week</h2>
        
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Week 1: Awareness & Setup</h3>
            <ul className="mt-2 space-y-2 text-gray-600 dark:text-gray-300 text-sm">
              <li className="flex items-start">
                <span className="text-sm mr-2">•</span> Complete your spending trigger assessment
              </li>
              <li className="flex items-start">
                <span className="text-sm mr-2">•</span> Establish your customized no-spend rules
              </li>
              <li className="flex items-start">
                <span className="text-sm mr-2">•</span> Set up your environment for success
              </li>
              <li className="flex items-start">
                <span className="text-sm mr-2">•</span> Learn to identify and log shopping urges
              </li>
            </ul>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Week 2: Navigating Challenges</h3>
            <ul className="mt-2 space-y-2 text-gray-600 dark:text-gray-300 text-sm">
              <li className="flex items-start">
                <span className="text-sm mr-2">•</span> Develop strategies for common trigger situations
              </li>
              <li className="flex items-start">
                <span className="text-sm mr-2">•</span> Build your personal urge-surfing toolkit
              </li>
              <li className="flex items-start">
                <span className="text-sm mr-2">•</span> Discover free alternatives to shopping-as-entertainment
              </li>
              <li className="flex items-start">
                <span className="text-sm mr-2">•</span> Complete the "unsubscribe challenge"
              </li>
            </ul>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Week 3: Deepening Awareness</h3>
            <ul className="mt-2 space-y-2 text-gray-600 dark:text-gray-300 text-sm">
              <li className="flex items-start">
                <span className="text-sm mr-2">•</span> Explore the emotions beneath spending impulses
              </li>
              <li className="flex items-start">
                <span className="text-sm mr-2">•</span> Practice mindfulness in consumer environments
              </li>
              <li className="flex items-start">
                <span className="text-sm mr-2">•</span> Evaluate your existing possessions with fresh eyes
              </li>
              <li className="flex items-start">
                <span className="text-sm mr-2">•</span> Create a values-based spending framework
              </li>
            </ul>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Week 4: Integration & Beyond</h3>
            <ul className="mt-2 space-y-2 text-gray-600 dark:text-gray-300 text-sm">
              <li className="flex items-start">
                <span className="text-sm mr-2">•</span> Review insights and celebrate achievements
              </li>
              <li className="flex items-start">
                <span className="text-sm mr-2">•</span> Create your post-challenge spending plan
              </li>
              <li className="flex items-start">
                <span className="text-sm mr-2">•</span> Set up sustainable habits and boundaries
              </li>
              <li className="flex items-start">
                <span className="text-sm mr-2">•</span> Receive your personalized maintenance strategy
              </li>
            </ul>
          </div>
        </div>
      </motion.section>
      
      {/* Testimonials section */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Success Stories From Challenge Graduates</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "I discovered I was using online shopping as my default activity whenever I felt bored or anxious. The challenge helped me find better ways to manage these emotions. Six months later, I'm still spending 60% less than before!"
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Jamie K.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Saved $1,430 during challenge</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "I never realized how much my consumption habits were driven by social media and marketing. The daily reflections helped me see these influences clearly. Now I feel in control of my purchasing decisions instead of being manipulated."
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Marcus T.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Completed 3 consecutive challenges</p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>
      
      {/* FAQ Section */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Common Questions About The No-Spend Challenge</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">What exactly counts as "no spending"?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              You'll define what "no-spend" means for your specific situation during setup. Typically, participants continue spending on true necessities (groceries, bills, transportation) while eliminating discretionary purchases (clothing, entertainment, dining out, impulse buys). Our guided setup helps you create clear, personalized rules.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">What if I need to make an exception during the challenge?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Life happens! The challenge includes a structured decision-making framework for potential exceptions. Rather than viewing exceptions as failures, you'll learn to evaluate them mindfully, document your reasoning, and maintain your commitment to the overall challenge.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How much time does the challenge require each day?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              The core daily activities take just 5-10 minutes. This includes checking in, completing micro-challenges, and logging any shopping urges. Some participants choose to engage more deeply with the optional reflection prompts and community features, but the basic commitment is minimal.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">What if I "fail" or break my no-spend rules?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              The challenge is designed to be imperfect! Breaking rules becomes a valuable learning opportunity rather than a failure. Our "slip recovery" protocol helps you analyze triggers, adjust strategies, and continue the challenge with renewed insight. Many participants report their "slips" were their most powerful learning moments.
            </p>
          </div>
        </div>
      </motion.section>
      
      {/* CTA section */}
      <motion.section 
        className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 rounded-xl p-6 text-center"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-3 dark:text-white">Ready For Your 30-Day Reset?</h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          Join thousands who have transformed their relationship with spending through the structured No-Spend Challenge.
        </p>
        <motion.button
          onClick={goToInput}
          className={`px-8 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Start Your Challenge Now
        </motion.button>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">
          All data stays private and stored only on your device.
        </p>
      </motion.section>
      
      {/* Rich SEO footer content */}
      <footer className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          <h2 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-200">About The 30-Day No-Spend Challenge</h2>
          <p className="mb-3">
            The 30-Day No-Spend Challenge is a structured program designed to help participants break the cycle of unnecessary spending by eliminating non-essential purchases for one month. This intentional pause creates space to examine spending triggers, develop healthier financial habits, and reset your relationship with consumption.
          </p>
          <p className="mb-3">
            Unlike typical budgeting approaches that focus only on numbers, our challenge addresses the psychological and emotional aspects of spending. Through daily micro-challenges, reflection prompts, and community support, participants develop awareness of their consumption patterns and build sustainable alternatives.
          </p>
          <p>
            Whether you're looking to save money, break shopping addiction patterns, reduce environmental impact, or simply gain more control over your financial life, the 30-Day No-Spend Challenge provides a supportive framework for lasting change. Join thousands who have discovered the freedom that comes from mindful spending through this transformative month-long journey.
          </p>
        </div>
      </footer>
    </motion.div>
  );
};

export default NoSpendChallengeLanding;
